import * as React from 'react';
import { DialogProps } from '@bb-ui/react-library/dist/components/Dialog/Dialog.types';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@bb-ui/react-library/dist/components/Dialog';
import { DialogTitle } from '@bb-ui/react-library/dist/components/DialogTitle';
import { DialogContent } from '@bb-ui/react-library/dist/components/DialogContent';
import {
  DefaultButton,
  DialogActions,
  DialogContentText,
  PrimaryButton,
} from '@bb-ui/react-library';
import { useSnackbar } from 'hooks/useSnackbar';
import useRestApi from 'hooks/useRestApi';
import { apiUrl } from 'utils/apiUrl';

export interface UnbindReleaseStageConfigurationDialogProps extends DialogProps {
  onClose: () => void;
  onSuccess: () => void;
  releaseStageName: string;
  tenantId: string;
  releaseStageId: string;
}

export const UnbindReleaseStageConfigurationDialog: React.FunctionComponent<
  UnbindReleaseStageConfigurationDialogProps
> = (props) => {
  const { id, open, onClose, onSuccess, releaseStageName, tenantId, releaseStageId } = props;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const {
    clearFailedRequests,
    clearSucceededRequests,
    doDelete,
    failedRequests,
    succeededRequests,
  } = useRestApi(apiUrl('lct', `releaseStage/tenants/${tenantId}/stage`), { manual: true });

  function unbindReleaseStage(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    doDelete(releaseStageId);
  }

  React.useEffect(() => {
    if (succeededRequests.length > 0) {
      enqueueSnackbar(t('releaseStages.unbindReleaseStageDialog.succededMessage'), {
        variant: 'success',
      });
      clearSucceededRequests();
      onClose();
      onSuccess();
    }
  }, [clearSucceededRequests, onClose, enqueueSnackbar, onSuccess, succeededRequests.length, t]);

  React.useEffect(() => {
    if (failedRequests.length > 0) {
      enqueueSnackbar(t('releaseStages.unbindReleaseStageDialog.errorMessage'), {
        variant: 'error',
      });
      clearFailedRequests();
      onClose();
    }
  }, [
    clearFailedRequests,
    onClose,
    enqueueSnackbar,
    failedRequests,
    failedRequests.length,
    t,
    releaseStageName,
  ]);

  return (
    <Dialog
      id={id}
      open={open}
      onClose={() => onClose()}
      data-testid={id}
      aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-description`}
    >
      <form onSubmit={unbindReleaseStage} noValidate data-testid="unbind-release-stage-form">
        <DialogTitle onClose={() => onClose()} id={`${id}-title`} closeLabel={t('global.close')}>
          {t('releaseStages.unbindReleaseStageDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id={`${id}-release-stage-unbind-content-text`}
            data-testid={`${id}-release-stage-unbind-content-text`}
          >
            {t('releaseStages.unbindReleaseStageDialog.content', {
              ReleaseStageName: releaseStageName,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <PrimaryButton type="submit">
            {t('releaseStages.unbindReleaseStageDialog.confirmDeleteReleaseStage')}
          </PrimaryButton>
          <DefaultButton onClick={() => onClose()}>
            {t('releaseStages.unbindReleaseStageDialog.cancelDeleteReleaseStage')}
          </DefaultButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
export default UnbindReleaseStageConfigurationDialog;
